/**
 * Utility functions for processing WordPress content
 */

/**
 * Parse WordPress Gutenberg block content and convert to clean HTML
 * @param {string} content - Raw WordPress content with blocks
 * @returns {string} - Clean HTML content
 */
export const parseWordPressContent = (content) => {
  if (!content) return '';

  // Remove Gutenberg block comments
  let cleanContent = content
    // Remove block comments like <!-- wp:heading {\"level\":3} --> and <!-- /wp:heading -->
    .replace(/<!-- wp:[^>]*-->/g, '')
    .replace(/<!-- \/wp:[^>]*-->/g, '')
    
    // Remove HTML block wrappers
    .replace(/<!-- wp:html -->/g, '')
    .replace(/<!-- \/wp:html -->/g, '')
    
    // Remove other WordPress block markers
    .replace(/<!-- wp:paragraph -->/g, '')
    .replace(/<!-- \/wp:paragraph -->/g, '')
    .replace(/<!-- wp:list -->/g, '')
    .replace(/<!-- \/wp:list -->/g, '')
    .replace(/<!-- wp:image[^>]*-->/g, '')
    .replace(/<!-- \/wp:image -->/g, '')
    .replace(/<!-- wp:quote[^>]*-->/g, '')
    .replace(/<!-- \/wp:quote -->/g, '')
    .replace(/<!-- wp:separator[^>]*-->/g, '<hr>')
    .replace(/<!-- \/wp:separator -->/g, '')
    
    // Handle shortcodes - convert to placeholder or remove
    .replace(/\[([^\]]+)\]/g, (match, shortcode) => {
      // Handle specific shortcodes
      if (shortcode.startsWith('contact-form')) {
        return '<div class="contact-form-placeholder"><p><em>Contact form would appear here</em></p></div>';
      }
      if (shortcode.startsWith('gallery')) {
        return '<div class="gallery-placeholder"><p><em>Photo gallery would appear here</em></p></div>';
      }
      if (shortcode.startsWith('ninja-forms')) {
        return '<div class="form-placeholder"><p><em>Contact form would appear here</em></p></div>';
      }
      // Remove unknown shortcodes
      return '';
    })
    
    // Clean up excessive whitespace and newlines
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .replace(/^\s+|\s+$/g, '')
    
    // Ensure paragraphs are properly wrapped
    .split('\n\n')
    .map(paragraph => {
      paragraph = paragraph.trim();
      if (paragraph && !paragraph.startsWith('<') && paragraph.length > 0) {
        return `<p>${paragraph}</p>`;
      }
      return paragraph;
    })
    .join('\n\n');

  return cleanContent;
};

/**
 * Extract excerpt from WordPress content
 * @param {string} content - Raw WordPress content
 * @param {number} maxLength - Maximum length of excerpt
 * @returns {string} - Clean text excerpt
 */
export const extractExcerpt = (content, maxLength = 160) => {
  if (!content) return '';

  // First clean the content
  const cleanContent = parseWordPressContent(content);
  
  // Strip all HTML tags
  const textContent = cleanContent
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  if (textContent.length <= maxLength) {
    return textContent;
  }
  
  // Truncate at word boundary
  const truncated = textContent.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
};

/**
 * Check if content contains media (images, videos, etc.)
 * @param {string} content - WordPress content
 * @returns {boolean} - True if content contains media
 */
export const hasMedia = (content) => {
  if (!content) return false;
  
  return /(<img|<video|<audio|<iframe|wp:image|wp:video|wp:audio)/i.test(content);
};

/**
 * Extract featured image from content if none is set
 * @param {string} content - WordPress content
 * @returns {string|null} - URL of first image found or null
 */
export const extractFeaturedImage = (content) => {
  if (!content) return null;
  
  const imgMatch = content.match(/<img[^>]+src="([^"]+)"[^>]*>/i);
  return imgMatch ? imgMatch[1] : null;
};

/**
 * Sanitize WordPress content for safe display
 * @param {string} content - Raw WordPress content
 * @returns {string} - Sanitized HTML content
 */
export const sanitizeContent = (content) => {
  if (!content) return '';
  
  const cleanContent = parseWordPressContent(content);
  
  // Basic XSS protection - remove script tags and javascript: links
  return cleanContent
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '');
};